import api from './api';
import { LoginRequest, LoginResponse, LoginChallengeResponse, User } from '../types';

const USER_KEY = 'user';

export const authService = {
  // ── Auth API calls ────────────────────────────────────────────────────────

  async login(credentials: LoginRequest): Promise<LoginChallengeResponse> {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  /**
   * Exchange the OTP for a session. The backend sets the JWT as an HttpOnly
   * cookie — this method only receives (and returns) the safe user object.
   */
  async verifyLogin(verificationId: string, code: string): Promise<LoginResponse> {
    const response = await api.post('/auth/login/verify', { verificationId, code });
    return response.data; // { user }
  },

  async register(data: {
    empNo: string;
    fullName: string;
    department: string;
    branch: string;
    email: string;
    password: string;
    telNo?: string;
  }) {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  async getProfile(): Promise<User> {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  /**
   * Ask the backend to issue a fresh cookie. Call this proactively
   * before the 24-hour expiry if you add a sliding-session timer.
   */
  async refreshToken(): Promise<void> {
    await api.post('/auth/refresh');
  },

  async forgotPassword(email: string) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token: string, newPassword: string) {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  /**
   * Tell the backend to clear the HttpOnly cookie, then wipe local state
   * and redirect to login.
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if the network call fails, we still clear local state
    } finally {
      authService.clearUser();
      window.location.href = '/login';
    }
  },

  // ── Local user-object cache (not the JWT — that stays in the cookie) ──────

  getUser(): User | null {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? (JSON.parse(stored) as User) : null;
    } catch {
      return null;
    }
  },

  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clearUser(): void {
    localStorage.removeItem(USER_KEY);
  },

  /** Optimistic check: true if we have a cached user object. The real
   *  source-of-truth is whether the cookie is still valid (verified on mount). */
  isAuthenticated(): boolean {
    return !!authService.getUser();
  },
};
